<template>
    <div class="el-main-content">
        <div class="el-grid-card">
            <div class="el-grid-card__header">{{ $t('title.overview') }}</div>
            <div class="el-grid-card__body">
                <el-row class="flex justify-between items-center filter-block">
                    <div>
                        <el-date-picker
                            id="listDateTimeRange"
                            v-model="dateTimeRange"
                            @change="fetchByDateTimeRange"
                            format="yyyy-MM-dd HH:mm"
                            type="datetimerange"
                            range-separator="-"
                            :clearable="false"
                            :picker-options="pickerOptions"
                            start-placeholder="Date, time from"
                            end-placeholder="Date, time until"
                            class="el-date-picker-light"
                        >
                        </el-date-picker>
                    </div>
                    <div>
                        <v-btn-toggle v-model="activeButton">
                            <v-btn
                                flat
                                color="dark"
                                @click="fetchByDateTimeRange(withinDay)"
                            >
                                {{ $t('button.text.today') }}
                            </v-btn>
                            <v-btn
                                flat
                                color="dark"
                                @click="fetchByDateTimeRange(withinFiveDays)"
                            >
                                {{ $t('button.text.days', { number: 5 }) }}
                            </v-btn>
                            <v-btn
                                flat
                                color="dark"
                                @click="fetchByDateTimeRange(withinSevenDays)"
                            >
                                {{ $t('button.text.days', { number: 7 }) }}
                            </v-btn>
                        </v-btn-toggle>
                    </div>
                </el-row>

                <el-row>
                    <div class="el-check__inline">
                        <div class="el-checkbox-group">
                            <v-checkbox
                                v-for="unloadingArea in unloadingAreas"
                                :label="unloadingArea.ShortDescription"
                                :key="unloadingArea.UnloadingAreaID"
                                :value="unloadingArea.ShortDescription"
                                v-model="checkList"
                                color="success"
                                hide-details
                            >
                            </v-checkbox>
                            <v-checkbox
                                v-if="
                                    selectedContainer.AvailableBookingTypes.includes(
                                        'PASS'
                                    )
                                "
                                :label="$t('table.filter.text.pass')"
                                v-model="checkList"
                                color="success"
                                :value="null"
                                hide-details
                            >
                            </v-checkbox>
                        </div>
                    </div>
                </el-row>

                <el-row>
                    <el-table
                        :data="bookings"
                        ref="bookingsTable"
                        row-key="ActorID"
                        size="mini"
                        :row-class-name="classForBookingOverlap"
                        class="w-full mb-6"
                        v-loading="loading"
                        border
                    >
                        <el-table-column type="expand" width="30">
                            <div :key="row.ActorID" slot-scope="{ row }">
                                <el-row :gutter="20">
                                    <el-col :span="8">
                                        <p>
                                            {{
                                                $t(
                                                    'table.data.label.mobileAppEnabled'
                                                )
                                            }}:
                                            {{
                                                row.MAIsEnabled
                                                    ? $t('text.yes')
                                                    : $t('text.no')
                                            }}
                                        </p>
                                        <p>
                                            {{ $t('form.label.email') }}:
                                            {{ row.Email || 'n/a' }}
                                        </p>
                                        <p>
                                            {{ $t('table.data.label.phone') }}:
                                            {{ row.Phone || 'n/a' }}
                                        </p>
                                        <p>
                                            {{
                                                $t('table.data.label.pinCode')
                                            }}: {{ row.PinCode || 'n/a' }}
                                        </p>
                                        <p>
                                            {{
                                                $t(
                                                    'form.label.recipientCompany'
                                                )
                                            }}:
                                            {{
                                                row.AccessSets.length
                                                    ? row.AccessSets[0]
                                                        .RecipientCompany
                                                    : 'n/a'
                                            }}
                                        </p>
                                        <p>
                                            {{
                                                $t('form.label.recipientPhone')
                                            }}:
                                            {{
                                                row.AccessSets.length
                                                    ? row.AccessSets[0]
                                                        .RecipientPhone
                                                    : 'n/a'
                                            }}
                                        </p>
                                        <p>
                                            {{
                                                $t(
                                                    'table.data.label.usageTimes'
                                                )
                                            }}: {{ row.UsageTimes || 0 }}
                                        </p>
                                    </el-col>
                                    <el-col :span="16">
                                        <p>
                                            {{
                                                $t('table.data.label.preFlex')
                                            }}:
                                            {{
                                                row.AccessSets.length
                                                    ? row.AccessSets[0][
                                                        'Schedules'
                                                    ][0]['ScheduleParts'][0][
                                                        'PreFlex'
                                                    ]
                                                    : 0
                                            }}
                                        </p>
                                        <p>
                                            {{
                                                $t(
                                                    'table.data.label.maxUsageTimes'
                                                )
                                            }}: {{ row.MaxUsageTimes || 0 }}
                                        </p>
                                        <p>
                                            {{
                                                $t(
                                                    'table.data.label.timeSlotInMins'
                                                )
                                            }}: {{ row.TimeSlotInMins || 0 }}
                                        </p>
                                        <p>
                                            {{
                                                $t(
                                                    'table.data.label.unloadingTimeInMins'
                                                )
                                            }}:
                                            {{ row.UnloadingTimeInMins || 0 }}
                                        </p>
                                        <p>
                                            {{
                                                $t('table.data.label.comment')
                                            }}: {{ row.Comment || 'n/a' }}
                                        </p>
                                    </el-col>
                                </el-row>
                            </div>
                        </el-table-column>
                        <el-table-column
                            sortable
                            :sort-method="
                                (a, b) => sortByDate(a, b, 'DateFrom')
                            "
                            prop="DateFrom"
                            :label="$t('table.column.dateFrom')"
                            width="150"
                        ></el-table-column>
                        <el-table-column
                            sortable
                            :sort-method="(a, b) => sortByDate(a, b, 'DateTo')"
                            prop="DateTo"
                            :label="$t('table.column.dateUntil')"
                            width="150"
                        ></el-table-column>
                        <el-table-column
                            sortable
                            prop="UnloadingArea"
                            :label="$t('table.column.unloadingArea')"
                            width="150"
                        >
                            <template slot-scope="scope">
                                <span>{{
                                    scope.row.UnloadingArea ||
                                        $t('table.filter.text.pass')
                                }}</span>
                            </template>
                        </el-table-column>
                        <el-table-column
                            sortable
                            prop="Entrance"
                            :label="$t('table.column.entrance')"
                            width="150"
                        ></el-table-column>
                        <el-table-column
                            sortable
                            prop="Exit"
                            :label="$t('table.column.exit')"
                            width="150"
                        ></el-table-column>
                        <el-table-column
                            sortable
                            prop="RecipientName"
                            :label="$t('table.column.recipientName')"
                        ></el-table-column>
                        <el-table-column
                            :label="$t('table.column.operations')"
                            width="150"
                            :resizable="false"
                        >
                            <template slot-scope="{ row }">
                                <div class="btn-action-wrapper">
                                    <a
                                        href="#"
                                        class="btn-action"
                                        @click.prevent="edit(row)"
                                        v-if="
                                            permissions.includes('ACTOR_WRITE')
                                        "
                                    >
                                        <v-btn flat icon color="cyan darken-3">
                                            <v-icon>mode_edit</v-icon>
                                        </v-btn>
                                    </a>
                                    <a
                                        href="#"
                                        class="btn-action"
                                        v-if="
                                            permissions.includes('ACTOR_DELETE')
                                        "
                                        @click.prevent="remove(row)"
                                    >
                                        <v-btn flat icon color="red darken-3">
                                            <v-icon>delete</v-icon>
                                        </v-btn>
                                    </a>
                                </div>
                            </template>
                        </el-table-column>
                    </el-table>
                </el-row>
            </div>
        </div>
    </div>
</template>

<script>
import _ from 'lodash'
import { DateTime } from 'luxon'
import { mapGetters } from 'vuex'
import InteractWithSort from '../../mixins/interact-with-sort'
import InteractWithActor from '../../mixins/interact-with-actor'
import InteractWithDates from '../../mixins/interact-with-dates'
import InteractWithUnloadingAreas from '../../mixins/interact-with-unloading-areas'

export default {
    name: 'booking-list',

    data() {
        return {
            activeButton: 0,
            loading: false,
            data: [],
            checkList: [],
            dateTimeRange: [],
        }
    },

    mixins: [
        InteractWithActor,
        InteractWithDates,
        InteractWithUnloadingAreas,
        InteractWithSort,
    ],

    computed: {
        pickerOptions() {
            return {
                firstDayOfWeek: 1,
            }
        },

        bookings() {
            let bookings = this.data
                .filter(item => item.ActorType !== 'MILKRUN')
                .map(item => {
                    let accessSet = item.AccessSets[0]
                    let schedulePart = accessSet.Schedules[0].ScheduleParts[0]

                    let validFrom = DateTime.fromISO(schedulePart.ValidFrom)
                    let validTo = DateTime.fromISO(schedulePart.ValidTo)

                    item['DateFrom'] = validFrom.toLocaleString(
                        DateTime.DATETIME_MED
                    )
                    item['DateTo'] = validTo.toLocaleString(
                        DateTime.DATETIME_MED
                    )

                    item['UnloadingArea'] = accessSet.UnloadingArea
                    item['RecipientName'] = accessSet.RecipientName

                    let entrance = _.find(item.AccessSets, {
                        Schedules: [{ ScheduleParts: [{ Direction: 'IN' }] }],
                    })
                    let exit = _.find(item.AccessSets, {
                        Schedules: [{ ScheduleParts: [{ Direction: 'OUT' }] }],
                    })

                    item['Entrance'] = entrance ? entrance['Checkpoint'] : ''
                    item['Exit'] = exit ? exit['Checkpoint'] : ''

                    return item
                })

            return bookings.filter(item => {
                if (!this.checkList.length) {
                    return true
                }

                return this.checkList.some(check => {
                    return check == item.UnloadingArea
                })
            })
        },

        ...mapGetters(['selectedContainer', 'permissions']),
    },

    created() {
        this.setInitialChecklist()
        this.fetch()

        Events.$on('date-range-changed', range => {
            this.dateTimeRange = range

            this.fetchByDateTimeRange(range)
        })
    },

    watch: {
        selectedContainer(container) {
            this.setInitialChecklist()
            this.fetch(container, this.dateTimeRange)
        },
    },

    methods: {
        async setInitialChecklist() {
            await this.fetchUnloadingAreas()

            let unloadingAreas = this.unloadingAreas.map(item => {
                return item.ShortDescription
            })
            unloadingAreas.push(null)

            this.checkList = unloadingAreas
        },

        classForBookingOverlap({ row }) {
            if (row.IsConflict) {
                return 'conflict-row'
            }

            return ''
        },

        edit(actor) {
            Events.$emit('actor-editing', actor)
        },

        fetchByDateTimeRange(range) {
            this.activeButton = ''
            this.fetch(this.selectedContainer, range)
        },

        async fetch(container = null, range = []) {
            this.loading = true

            container = container ? container : this.selectedContainer

            if (!_.isEmpty(container)) {
                range = range.length ? range : this.withinDay

                let dateTimeFormat = process.env.VUE_APP_DATETIME_FORMAT
                let dateTimeFrom = DateTime.fromISO(range[0].toISOString())
                let dateTimeTo = DateTime.fromISO(range[1].toISOString())

                // set end and start time to day start and day end if it doesn't contains time
                if (
                    dateTimeFrom.startOf('day').valueOf() ===
                        dateTimeFrom.valueOf() &&
                    dateTimeTo.startOf('day').valueOf() === dateTimeTo.valueOf()
                ) {
                    let start = dateTimeFrom.startOf('day')
                    let end = dateTimeTo.endOf('day')

                    dateTimeFrom = start.toFormat(dateTimeFormat)
                    dateTimeTo = end.toFormat(dateTimeFormat)

                    range = [start.toJSDate(), end.toJSDate()]
                } else {
                    dateTimeFrom = dateTimeFrom.toFormat(dateTimeFormat)
                    dateTimeTo = dateTimeTo.toFormat(dateTimeFormat)
                }

                let { data } = await this.$http.post(`Actor/Search`, {
                    ValidFrom: dateTimeFrom,
                    ValidTo: dateTimeTo,
                    ActorTypeFilter: ['PASS', 'SLOT', 'MILKRUN'],
                    DestinationContainerFilter: [container.ContainerID],
                })

                this.data = data
                this.dateTimeRange = range
            }

            this.loading = false
        },

        async remove(actor) {
            try {
                await this.$confirm(
                    this.$i18n.i18next.t('message.warning.delete', {
                        section: this.$i18n.i18next.t('text.booking'),
                    }),
                    'Warning',
                    {
                        confirmButtonText: this.$i18n.i18next.t(
                            'button.text.confirm'
                        ),
                        cancelButtonText: this.$i18n.i18next.t(
                            'button.text.cancel'
                        ),
                        type: 'warning',
                    }
                )

                await this.$http.delete(`Actor/${actor.ActorID}`)
                await this.fetch(this.selectedContainer, this.dateTimeRange)

                this.$message({
                    type: 'success',
                    showClose: true,
                    message: this.$i18n.i18next.t('message.success.deleted', {
                        section: this.$i18n.i18next.t('text.booking'),
                    }),
                })
            } catch (e) {
                if (e === 'cancel') {
                    //
                } else if (e.response.status == 488) {
                    this.$message({
                        type: 'error',
                        showClose: true,
                        message: e.response.data,
                    })
                } else {
                    this.$message({
                        type: 'error',
                        showClose: true,
                        message: this.$i18n.i18next.t('message.error.general'),
                    })
                }
            }
        },
    },
}
</script>

<style lang="scss" scoped>
.el-table tr.conflict-row {
    background: oldlace !important;
}

.el-table__expanded-cell {
    p {
        padding: 5px;
    }
}

.filters {
    .el-checkbox + .el-checkbox {
        margin-left: 0 !important;
    }
}
</style>
